import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import CommonNestedTable from '@/components/common/commonTable/CommonNestedTable';
import { TableColumn } from '@/components/common/commonTable/CommonTable';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Card from '@/components/UI/Card';
import { useDebounce } from '@/hooks/useDebounce';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import {
  ICategoryData,
  ICategoryResponse,
} from '@/interfaces/categoryResponse.interface';
import { PageInfo } from '@/interfaces/common.interface';
import { GetFilterUrl } from '@/utility/helper';
import React, { useEffect, useState } from 'react';
import FormField from '../UI/FormField';
import ImagePreview from '../common/media/ImagePreview';
import PageHeading from '../common/PageHeading';

interface CategoryTableProp {
  onSelectCtegory?: (selected: ICategoryData[]) => void;
  selectionMode?: 'single' | 'multiple';
  initialSelectedIds?: Array<string | number>;
}

interface CategoryStates {
  isLoading: boolean;
  categoryStatus: string;
  categories: ICategoryData[];
  pageInfo: PageInfo;
}

const CategoryTable: React.FC<CategoryTableProp> = ({
  onSelectCtegory,
  selectionMode = 'multiple',
  initialSelectedIds = [],
}) => {
  const [searchValue, setSearchValue] = useState('');
  const debounceValue = useDebounce(searchValue);

  const [states, setStates] = useState<CategoryStates>({
    isLoading: false,
    categoryStatus: 'active',
    categories: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
  });

  const handleChangeState = (newState: Partial<CategoryStates>) => {
    setStates((prev) => ({ ...prev, ...newState }));
  };

  /* ------------------ API ------------------ */
  const fetchCategories = async () => {
    try {
      handleChangeState({ isLoading: true });

      const query = GetFilterUrl({
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        status: states.categoryStatus,
        search: debounceValue,
      });

      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}${
        query ? `?${query}` : ''
      }`;

      const res = await callApi<ICategoryResponse>(PATH, { method: 'GET' });

      handleChangeState({
        categories: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      console.error(error);
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [
    states.pageInfo.page,
    states.pageInfo.limit,
    states.categoryStatus,
    debounceValue,
  ]);

  /* ------------------ Selection ------------------ */
  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.categories);

  const handleSelectCategory = (category: ICategoryData) => {
    if (selectionMode === 'single') {
      handleSelectAll(false);
      handleSelectItem(category, true);
    } else {
      const isSelected = selectedItems.has(category.id);
      handleSelectItem(category, !isSelected);
    }
  };

  /* Emit selection to parent */
  useEffect(() => {
    const selectedData = states.categories.filter((c) =>
      selectedItems.has(c.id),
    );
    onSelectCtegory?.(selectedData);
  }, [selectedItems, states.categories]);

  /* Initial selection (single or multiple) */
  useEffect(() => {
    if (initialSelectedIds.length === 0 || states.categories.length === 0)
      return;

    handleSelectAll(false);

    const idsToSelect =
      selectionMode === 'single'
        ? initialSelectedIds.slice(0, 1)
        : initialSelectedIds;

    states.categories.forEach((category) => {
      if (idsToSelect.includes(category.id)) {
        handleSelectItem(category, true);
      }
    });
  }, [initialSelectedIds, states.categories]);

  /* ------------------ Columns ------------------ */
  const columns: TableColumn<ICategoryData>[] = [
    {
      key: 'name',
      header: 'Category',
      render: (item) => (
        <ImagePreview
          src={`${API_URL_PRODUCT_SERVICE}/${item?.media?.[0]?.url}`}
          alt=""
          text={item.name}
        />
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (item) => (
        <span className="inline-block max-w-[250px] truncate font-medium">
          {item.description}
        </span>
      ),
    },
    {
      key: 'productCount',
      header: 'Products',
    },
  ];

  const childColumns: TableColumn<ICategoryData>[] = [
    {
      key: 'name',
      header: 'Sub Category',
      className: 'p-3',
    },
    {
      key: 'description',
      header: 'Description',
      className: 'p-3',
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown value={item.status} options={[]} onChange={() => {}} />
      ),
    },
  ];

  /* ------------------ Pagination ------------------ */
  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: { ...states.pageInfo, page },
    });
  };

  const handleSizeChange = (limit: number) => {
    handleChangeState({
      pageInfo: { ...states.pageInfo, limit },
    });
  };

  /* ------------------ UI ------------------ */
  return (
    <Card>
      <PageHeading text="Categories" />
      <div className="mb-2">
        <FormField
          placeholder="Search for the categories..."
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </div>

      <CommonNestedTable
        columns={columns}
        data={states.categories}
        showCheckbox
        selectedItems={selectedItems}
        onSelectAll={selectionMode === 'multiple' ? handleSelectAll : undefined}
        onSelectItem={handleSelectCategory}
        getItemId={(item) => item.id}
        emptyMessage="No Categories Found"
        loading={states.isLoading}
        getChildren={(item) => item.subcategories}
        childColumns={childColumns}
      />

      <Pagination
        paginationData={states.pageInfo}
        onPageChange={handleChangePage}
        isLoading={states.isLoading}
        onPageSizeChange={handleSizeChange}
      />
    </Card>
  );
};

export default CategoryTable;
